import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
import { ChevronDown } from "lucide-react";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

function DriverColumns({ option }) {
  return <span className="reports-driver-columns grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_6ch_7ch] items-center gap-x-2">
    <span className={`reports-driver-name min-w-0 break-words ${option.driverName ? "" : "col-span-3"}`}>{option.label}</span>
    {option.driverName ? <>
      <span className="reports-driver-truck min-w-0 break-words tabular-nums">{option.truckLabel || "—"}</span>
      <span className="reports-driver-trailer min-w-0 break-words tabular-nums">{option.trailerLabel || "—"}</span>
    </> : null}
  </span>;
}

export default function ReportsDriverSelect({ disabled = false, onChange, options, placeholder, value }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const triggerRef = React.useRef(null);
  const activeOptionRef = React.useRef(null);
  const listId = React.useId();
  const rows = [{ value: "", label: placeholder }, ...options];
  const selected = rows.find((option) => option.value === value) || rows[0];
  const selectedIndex = Math.max(0, rows.findIndex((option) => option.value === selected.value));
  const open = isOpen && !disabled;
  const close = React.useCallback(() => setIsOpen(false), []);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open,
    strategy: "fixed",
    placement: "bottom-start",
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 }), size({
      apply({ rects, elements, availableHeight }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
          maxHeight: `${Math.max(0, Math.min(320, availableHeight))}px`,
        });
      },
    })],
    whileElementsMounted: autoUpdate,
  });
  usePopoverDismiss({ anchorElement: triggerRef.current, floatingRef: refs.floating, isOpen: open, onClose: close });
  React.useEffect(close, [close, disabled, options]);
  React.useEffect(() => {
    if (open && isPositioned) activeOptionRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isPositioned, open]);
  const show = () => { setActiveIndex(selectedIndex); setIsOpen(true); };
  const choose = (option) => { if (option) onChange(option.value); close(); };
  const portalRoot = getReactPortalRoot();

  return <>
    <button
      ref={(element) => { triggerRef.current = element; refs.setReference(element); }}
      type="button"
      role="combobox"
      aria-label="Chauffeur"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-activedescendant={open ? `${listId}-${activeIndex}` : undefined}
      data-value={selected.value}
      disabled={disabled}
      className="flex min-h-9 w-full min-w-0 items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-left text-[13px] font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none"
      onClick={() => open ? close() : show()}
      onBlur={close}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault(); event.stopPropagation(); close();
        } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
          event.preventDefault();
          if (!open) show();
          else setActiveIndex((index) => event.key === "Home" ? 0 : event.key === "End" ? rows.length - 1
            : Math.max(0, Math.min(rows.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
        } else if ((event.key === "Enter" || event.key === " ") && open) {
          event.preventDefault(); choose(rows[activeIndex]);
        }
      }}
    >
      <DriverColumns option={selected} />
      <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
    </button>
    {open && portalRoot ? createPortal(
      <ul
        ref={refs.setFloating}
        id={listId}
        role="listbox"
        aria-label="Chauffeur"
        className="reports-driver-list m-0 list-none overflow-y-auto rounded-[4px] border border-slate-300 bg-white px-0 py-1 text-[13px] font-medium text-slate-700 shadow-lg"
        style={{ ...floatingStyles, visibility: isPositioned ? "visible" : "hidden", zIndex: 2200 }}
      >
        {rows.map((option, index) => <li
          key={option.value}
          ref={index === activeIndex ? activeOptionRef : null}
          id={`${listId}-${index}`}
          role="option"
          aria-selected={option.value === selected.value}
          aria-label={option.driverName
            ? `${option.label}, camion ${option.truckLabel || "non renseigné"}, citerne ${option.trailerLabel || "non renseignée"}`
            : option.label}
          data-value={option.value}
          className={`flex min-h-8 cursor-pointer items-center py-1 pl-2.5 pr-8 text-left ${option.value === selected.value
            ? "bg-accent text-white hover:bg-accent-strong" : index === activeIndex ? "bg-slate-100" : "hover:bg-slate-50"}`}
          onMouseDown={(event) => event.preventDefault()}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => choose(option)}
        ><DriverColumns option={option} /></li>)}
      </ul>, portalRoot,
    ) : null}
  </>;
}
